// src/views/LoginView.js — ACTUALIZADO 10 abril 2026: Selector idioma + textos localizados
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n';
import { Btn, Input } from '../components/UI';
import Icon from '../components/Icons';

// Selector de idioma inline (sin importar Layout para evitar dependencias circulares)
function LangSelector() {
  const { lang, setLang, LANGS } = useLang();
  return (
    <div style={{ position:'relative', display:'inline-block' }}>
      <select value={lang} onChange={e => setLang(e.target.value)} style={{
        appearance:'none', WebkitAppearance:'none',
        background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.15)',
        borderRadius:8, color:'#00CFC8',
        fontSize:12, fontWeight:700, fontFamily:'var(--font-display)',
        padding:'6px 28px 6px 10px', cursor:'pointer', letterSpacing:'0.05em',
      }}>
        {LANGS.map(l => (
          <option key={l.code} value={l.code} style={{background:'#0a1628'}}>{l.flag} {l.name}</option>
        ))}
      </select>
      <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)',
        pointerEvents:'none', color:'#00CFC8', fontSize:9, opacity:0.6 }}>&#9662;</span>
    </div>
  );
}


export default function LoginView() {
  const { t } = useLang();
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      const msgs = {
        'auth/invalid-credential': t('auth_error_credenciales') || 'Email o contrasena incorrectos.',
        'auth/user-not-found': t('auth_error_usuario') || 'No existe una cuenta con ese email.',
        'auth/wrong-password': t('auth_error_password') || 'Contrasena incorrecta.',
        'auth/too-many-requests': t('auth_error_intentos') || 'Demasiados intentos. Espera un momento.',
      };
      setError(msgs[err.code] || t('general_error') || 'Error al iniciar sesion.');
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!email) { setError(t('auth_introduce_email') || 'Introduce tu email.'); return; }
    setLoading(true);
    try {
      await resetPassword(email);
      setResetSent(true); setError('');
    } catch { setError(t('auth_error_reset') || 'No se pudo enviar el email.'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight:'100vh', background:'var(--bg-base)',
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:16,
    }}>
      {/* Selector idioma arriba a la derecha — visible ANTES de login */}
      <div style={{
        position:'fixed', top:16, right:16, zIndex:10,
      }}>
        <LangSelector />
      </div>

      {/* Background effect */}
      <div style={{
        position:'fixed', inset:0, zIndex:0, overflow:'hidden', pointerEvents:'none',
      }}>
        <div style={{
          position:'absolute', top:'-20%', left:'-10%',
          width:600, height:600, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(0,207,200,0.06) 0%, transparent 70%)',
        }} />
        <div style={{
          position:'absolute', bottom:'-20%', right:'-10%',
          width:500, height:500, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(10,110,250,0.06) 0%, transparent 70%)',
        }} />
      </div>

      <div style={{ position:'relative', zIndex:1, width:'100%', maxWidth:420 }}>
        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:40 }}>
          <img src="/logo_cities.png" alt="ACES Cities Network"
               style={{ height:48, width:'auto', objectFit:'contain', marginBottom:4 }}
               onError={e => { e.target.style.display='none'; }}/>
          <h1 style={{ fontSize:24, fontWeight:900, fontFamily:'var(--font-display)',
                       letterSpacing:'-0.03em', marginBottom:4 }}>
            ACES Cities Network
          </h1>
          <p style={{ fontSize:13, color:'var(--text-secondary)' }}>
            {t('login_subtitulo') || 'Panel de gestion institucional'}
          </p>
        </div>

        {/* Card */}
        <div style={{
          background:'var(--bg-card)', border:'1px solid var(--border)',
          borderRadius:'var(--radius-lg)', padding:28,
          boxShadow:'var(--shadow-card)',
        }}>
          {!resetMode ? (
            <>
              <h2 style={{ fontSize:17, fontWeight:700, fontFamily:'var(--font-display)',
                           marginBottom:24 }}>{t('auth_entrar')||"Acceder"}</h2>
              <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:16 }}>
                <Input
                  label="Email" icon="mail" type="email"
                  placeholder={t('email_placeholder')} value={email}
                  onChange={e => setEmail(e.target.value)} required
                />
                <div>
                  <label style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)',
                                  letterSpacing:'0.05em', textTransform:'uppercase', display:'block', marginBottom:6 }}>
                    {t('auth_password') || 'Contrasena'}
                  </label>
                  <div style={{ position:'relative' }}>
                    <Icon name="lock" size={16} style={{
                      position:'absolute', left:12, top:'50%', transform:'translateY(-50%)',
                      color:'var(--text-muted)', pointerEvents:'none',
                    }} />
                    <input type={showPwd ? 'text' : 'password'}
                           placeholder="..." value={password}
                           onChange={e => setPassword(e.target.value)} required
                           style={{ paddingLeft:40, paddingRight:40 }} />
                    <button type="button" onClick={() => setShowPwd(!showPwd)} style={{
                      position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
                      background:'none', color:'var(--text-muted)',
                    }}>
                      <Icon name={showPwd ? 'eyeOff' : 'eye'} size={16} />
                    </button>
                  </div>
                </div>

                {error && (
                  <div style={{
                    padding:'10px 12px', borderRadius:8,
                    background:'var(--red-soft)', border:'1px solid var(--red)33',
                    color:'var(--red)', fontSize:13, display:'flex', gap:8, alignItems:'center',
                  }}>
                    <Icon name="alert" size={14} color="var(--red)" /> {error}
                  </div>
                )}

                <Btn type="submit" loading={loading} style={{ width:'100%', justifyContent:'center', padding:'12px' }}>
                  {loading ? (t('auth_accediendo') || 'Accediendo...') : (t('auth_entrar') || 'Acceder')}
                </Btn>

                <button type="button" onClick={() => { setResetMode(true); setError(''); }}
                        style={{ background:'none', color:'var(--text-secondary)',
                                 fontSize:12, textAlign:'center', fontFamily:'var(--font-body)' }}>
                  {t('auth_olvido_password') || 'Olvidaste tu contrasena?'}
                </button>
              </form>
            </>
          ) : (
            <>
              <button onClick={() => { setResetMode(false); setResetSent(false); setError(''); }}
                      style={{ display:'flex', alignItems:'center', gap:6, color:'var(--text-secondary)',
                               background:'none', fontSize:12, marginBottom:20, fontFamily:'var(--font-body)' }}>
                <Icon name="chevronLeft" size={14} /> {t('general_volver') || 'Volver'}
              </button>
              <h2 style={{ fontSize:17, fontWeight:700, fontFamily:'var(--font-display)', marginBottom:8 }}>
                {t('auth_recuperar') || 'Recuperar acceso'}
              </h2>
              <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:20 }}>
                {t('auth_recuperar_desc') || 'Introduce tu email y te enviaremos un enlace para restablecer tu contrasena.'}
              </p>
              {resetSent ? (
                <div style={{
                  padding:'14px 16px', borderRadius:10,
                  background:'var(--green-soft)', border:'1px solid var(--green)33',
                  color:'var(--green)', fontSize:13, display:'flex', gap:8, alignItems:'center',
                }}>
                  <Icon name="check" size={16} color="var(--green)" />
                  {t('auth_email_enviado') || 'Email enviado. Revisa tu bandeja de entrada.'}
                </div>
              ) : (
                <form onSubmit={handleReset} style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  <Input label="Email" icon="mail" type="email"
                         placeholder={t('email_placeholder')} value={email}
                         onChange={e => setEmail(e.target.value)} required />
                  {error && (
                    <div style={{ color:'var(--red)', fontSize:12 }}>{error}</div>
                  )}
                  <Btn type="submit" loading={loading} style={{ width:'100%', justifyContent:'center', padding:'12px' }}>
                    {t('auth_enviar_enlace') || 'Enviar enlace'}
                  </Btn>
                </form>
              )}
            </>
          )}
        </div>

        <div style={{ textAlign:'center', marginTop:20 }}>
          <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:8 }}>
            {t('login_sin_cuenta')||'No tienes cuenta?'}
          </p>
          <Link to="/registro" style={{
            display:'inline-flex', alignItems:'center', gap:6, padding:'8px 20px',
            borderRadius:8, border:'1px solid rgba(0,207,200,.3)',
            color:'var(--cyan)', fontSize:13, fontWeight:600, textDecoration:'none',
            background:'rgba(0,207,200,.06)',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <line x1="19" y1="8" x2="19" y2="14"/>
              <line x1="22" y1="11" x2="16" y2="11"/>
            </svg>
            {t('crear_cuenta')||'Crear cuenta con codigo ACES'}
          </Link>
          <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:10 }}>
            {t('login_requiere_codigo')||'El acceso requiere un codigo de invitacion ACES.'}
          </p>
        </div>
      </div>
    </div>
  );
}
